import React, { useState, useEffect, useRef } from 'react';
import { 
  Users, Shield, Building, BarChart3, 
  CheckCircle, XCircle, Search, Filter, Download, Eye,
  AlertCircle, RefreshCw, BookOpen,
  Plus, Edit, Trash2, Save, X, UserPlus, FolderOpen, Calendar,
  Ban, History, UserX, Mail,
  FileText, GraduationCap, School,
  MessageSquare, Bell, Tag, Send, CheckCheck, Paperclip, ArrowLeft, Settings
} from 'lucide-react';
import { motion } from 'framer-motion';

import Footer from '../components/common/Footer';
import Button from '../components/common/Button';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { adminAPI, authAPI, chatAPI, publicAPI } from '../services/api';
import Header from '../components/common/Header';

const AdminPortal = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [searchQuery, setSearchQuery] = useState('');
  const [accessSearch, setAccessSearch] = useState('');
  const [accessUsers, setAccessUsers] = useState([]);
  const [accessPage, setAccessPage] = useState(1);
  const [accessTotal, setAccessTotal] = useState(0);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessSaving, setAccessSaving] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Data States
  const [pendingTeachers, setPendingTeachers] = useState([]);
  const [allTeachers, setAllTeachers] = useState([]);
  const [schoolAdmins, setSchoolAdmins] = useState([]);
  const [schools, setSchools] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState([]);
  const [showPasswordModal, setShowPasswordModal] = useState(null);
  
  // Inline approval states
  const [approvalPanels, setApprovalPanels] = useState({});
  const [approvalPrimaryDepts, setApprovalPrimaryDepts] = useState({});

  const openApprovalPanel = (teacherId, defaultDeptId) => {
    setApprovalPanels(prev => ({ ...prev, [teacherId]: prev[teacherId] !== undefined ? undefined : [] }));
    if (defaultDeptId) {
      setApprovalPrimaryDepts(prev => ({ ...prev, [teacherId]: defaultDeptId }));
    }
  };

  const toggleApprovalDept = (teacherId, deptId) => {
    setApprovalPanels(prev => {
      const current = prev[teacherId] || [];
      const next = current.includes(deptId) ? current.filter(id => id !== deptId) : [...current, deptId];
      return { ...prev, [teacherId]: next };
    });
  };

  // Year Management States
  const [yearManageSchool, setYearManageSchool] = useState('');
  const [yearManageDept, setYearManageDept] = useState(null);
  const [newYearInput, setNewYearInput] = useState('');
  
  // Form Visibility States
  const [showSchoolForm, setShowSchoolForm] = useState(false);
  const [showDepartmentForm, setShowDepartmentForm] = useState(false);
  const [showCourseForm, setShowCourseForm] = useState(false);
  const [showSchoolAdminForm, setShowSchoolAdminForm] = useState(false);
  
  // Form Data States
  const [schoolForm, setSchoolForm] = useState({ 
    name: '', 
    code: '', 
    description: '', 
    contactEmail: '', 
    contactPhone: '', 
    dean: '' 
  });
  
  const [departmentForm, setDepartmentForm] = useState({ 
    name: '', 
    code: '', 
    school: '', 
    headOfDepartment: '', 
    description: '', 
    contactEmail: '' 
  });
  
  const [courseForm, setCourseForm] = useState({ 
    title: '', 
    courseCode: '', 
    department: '', 
    description: '', 
    creditHours: 3, 
    semester: '1', 
    year: '1' 
  });
  
  const [schoolAdminForm, setSchoolAdminForm] = useState({
    fullName: '',
    email: '',
    school: '',
    employeeId: ''
  });
  
  const [editingItem, setEditingItem] = useState(null);
  const [editingSchoolAdmin, setEditingSchoolAdmin] = useState(null);
  
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);

  // Chat States
  const [contacts, setContacts] = useState([]);
  const [chatSearch, setChatSearch] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [chatAttachment, setChatAttachment] = useState(null);
  const chatFileInputRef = useRef(null);
  const [sendingMsg, setSendingMsg] = useState(false);
  const [loadingChat, setLoadingChat] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const messagesEndRef = useRef(null);

  // Authentication Check
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData) { 
      navigate('/'); 
      return; 
    }
    
    const parsedUser = JSON.parse(userData);
    if (parsedUser.role !== 'admin') { 
      navigate('/'); 
      return; 
    }
    
    setAdmin(parsedUser);
    loadDashboardData();
  }, [navigate]);

  // Chat Handlers & Effects
  const loadContacts = async (silent = false) => {
    try {
      if (!silent) setLoadingChat(true);
      const res = await chatAPI.getContacts();
      if (res.success) {
        setContacts(res.data || []);
      }
    } catch (err) {
      console.error('Failed to load chat contacts', err);
    } finally {
      if (!silent) setLoadingChat(false);
    }
  };

  const loadMessages = async (contactId, silent = false) => {
    try {
      if (!silent) setLoadingMessages(true);
      const res = await chatAPI.getMessages(contactId);
      if (res.success) {
        setMessages(res.data || []);
      }
    } catch (err) {
      console.error('Failed to load chat messages', err);
    } finally {
      if (!silent) setLoadingMessages(false);
    }
  };

  const handleSelectContact = async (contact) => {
    setSelectedContact(contact);
    setNewMessage('');
    await loadMessages(contact._id);
    await chatAPI.markAsRead(contact._id);
    loadContacts(true);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && !chatAttachment) return;
    if (!selectedContact) return;
    try {
      setSendingMsg(true);
      let res;
      if (chatAttachment) {
        const formData = new FormData();
        formData.append('receiverId', selectedContact._id);
        if (newMessage.trim()) formData.append('content', newMessage.trim());
        formData.append('attachment', chatAttachment);
        res = await chatAPI.sendMessage(formData);
      } else {
        res = await chatAPI.sendMessage({
          receiverId: selectedContact._id,
          content: newMessage.trim()
        });
      }
      if (res.success) {
        setMessages(prev => [...prev, res.data]);
        setNewMessage('');
        setChatAttachment(null);
        loadContacts(true);
      }
    } catch (err) {
      console.error('Failed to send message', err);
    } finally {
      setSendingMsg(false);
    }
  };



  useEffect(() => {
    if (activeTab === 'chat') {
      loadContacts();
    }
  }, [activeTab]);

  useEffect(() => {
    // Initial global load for badges
    loadContacts(true);
    
    // Background polling for new messages and badges
    const contactsInterval = setInterval(() => {
      loadContacts(true);
    }, 6000);
    return () => clearInterval(contactsInterval);
  }, []);

  useEffect(() => {
    let msgInterval;
    if (activeTab === 'chat' && selectedContact) {
      msgInterval = setInterval(() => {
        loadMessages(selectedContact._id, true);
      }, 3000);
    }
    return () => {
      if (msgInterval) clearInterval(msgInterval);
    };
  }, [activeTab, selectedContact]);

  const totalUnreadChat = contacts.reduce((sum, c) => sum + (c.unreadCount || 0), 0);

  const location = useLocation();

  // Tab Sync with URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab) {
      setActiveTab(tab);
    }
  }, [location.search]);

  // --- DATA LOADERS ---
  const loadDashboardData = async () => {
    try {
      setLoading(true);
      loadDepartments();
      const [statsData, approvalsData, feedbackData] = await Promise.all([
        adminAPI.getDashboardStats(),
        adminAPI.getPendingApprovals({ limit: 5 }),
        adminAPI.getFeedback({ excludeSchools: true })
      ]);
      
      // Update feedback state
      setFeedback(feedbackData.data?.data || feedbackData.data || []);
      
      // Format stats for display
      const statsArray = [];
      if (statsData.data && statsData.data.stats) {
        const { stats: statsObj } = statsData.data;
        statsArray.push(
          { label: "Total Teachers", value: statsObj.totalTeachers?.value || 0 },
          { label: "Pending Approvals", value: statsObj.pendingApprovals?.value || 0 },
          { label: "Total Materials", value: statsObj.totalMaterials?.value || 0 },
          { label: "Active Schools", value: statsObj.activeSchools?.value || 0 }
        );
      } else {
        statsArray.push(
          { label: "Total Teachers", value: 0 },
          { label: "Pending Approvals", value: 0 },
          { label: "Total Materials", value: 0 },
          { label: "Active Schools", value: 0 }
        );
      }
      
      setStats(statsArray);
      
      // Process pending teachers
      let requests = [];
      if (approvalsData.data) {
        if (approvalsData.data.requests) {
          requests = approvalsData.data.requests;
        } else if (approvalsData.data.data && approvalsData.data.data.requests) {
          requests = approvalsData.data.data.requests;
        }
      }
      
      setPendingTeachers(requests);
      setError('');
    } catch (err) {
      console.error('Load dashboard error:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadApprovalsTab = async () => {
    try {
      setLoading(true);
      loadDepartments();
      const response = await adminAPI.getPendingApprovals({ 
        search: searchQuery,
        limit: 20 
      });
      
      let requests = [];
      if (response.data && response.data.requests) {
        requests = response.data.requests;
      } else if (response.data && response.data.data && response.data.data.requests) {
        requests = response.data.data.requests;
      } else if (Array.isArray(response.data)) {
        requests = response.data;
      }
      
      setPendingTeachers(requests);
      setError('');
    } catch (err) { 
      console.error('Load approvals error:', err);
      setError('Failed to load pending approvals'); 
      setPendingTeachers([]);
    } finally { 
      setLoading(false); 
    }
  };

  const loadAllTeachers = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllTeachers({ search: searchQuery });
      const teachers = response.data?.teachers || response.data?.data?.teachers || [];
      const activeTeachers = teachers.filter(teacher => 
        teacher.role === 'teacher' && 
        teacher.registrationStatus !== 'rejected'
      );
      setAllTeachers(activeTeachers);
      setError('');
    } catch (err) { 
      console.error('Load teachers error:', err);
      setError('Failed to load teachers'); 
      setAllTeachers([]);
    } finally { 
      setLoading(false); 
    }
  };

  const loadSchoolAdmins = async () => {
  try {
    setLoading(true);
    const response = await adminAPI.getSchoolAdmins({ search: searchQuery });
    console.log('School admins response:', response); // Debug: Check what's returned
    
    // Handle different response structures
    const admins = response.data?.admins || response.data?.data?.admins || response.data || [];
    setSchoolAdmins(admins);
    setError('');
  } catch (err) {
    console.error('Load school admins error:', err);
    setError('Failed to load school admins: ' + (err.message || 'Unknown error'));
    setSchoolAdmins([]);
  } finally {
    setLoading(false);
  }
};

  const loadSchoolsTab = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getSchools({ search: searchQuery });
      const schoolData = response.data || response.data?.data || [];
      const transformedSchools = schoolData.map((school) => ({
        id: school._id || school.id, 
        name: school.name,
        code: school.code,
        departments: school.departments?.length || 0,
        materials: 0,
        status: school.isActive ? 'active' : 'pending',
        description: school.description,
        dean: school.dean
      }));
      setSchools(transformedSchools);
      setError('');
    } catch (err) { 
      console.error('Load schools error:', err);
      setError('Failed to load schools data'); 
      setSchools([]);
    } finally { 
      setLoading(false); 
    }
  };

  async function loadDepartments() {
    try {
      setLoading(true);
      const response = await adminAPI.getDepartments({ search: searchQuery });
      setDepartments(response.data || []);
      setError('');
    } catch (err) { 
      console.error('Load departments error:', err);
      setError('Failed to load departments'); 
      setDepartments([]);
    } finally { 
      setLoading(false); 
    }
  }

  const loadFeedback = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getFeedback({ excludeSchools: true });
      setFeedback(response.data?.data || response.data || []);
      setError('');
    } catch (err) {
      console.error('Load feedback error:', err);
      setError('Failed to load feedback messages');
      setFeedback([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getCourses({ search: searchQuery });
      setCourses(response.data || []);
      setError('');
    } catch (err) { 
      console.error('Load courses error:', err);
      setError('Failed to load courses'); 
      setCourses([]);
    } finally { 
      setLoading(false); 
    }
  };

  const loadAccessManagedUsers = async () => {
    try {
      setAccessLoading(true);
      const response = await adminAPI.getAccessManagedUsers({
        search: accessSearch,
        page: accessPage,
        limit: 25
      });
      setAccessUsers(response.data || []);
      setAccessTotal(response.pagination?.total || 0);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load access settings');
      setAccessUsers([]);
    } finally {
      setAccessLoading(false);
    }
  };

  const updateAccess = async (userId, updates) => {
    setAccessSaving(prev => ({ ...prev, [userId]: true }));
    try {
      const response = await adminAPI.updateUserAccess(userId, updates);
      setAccessUsers(prev => prev.map(user => user._id === userId ? response.data : user));
      setSuccess('Access settings updated.');
      setTimeout(() => setSuccess(''), 2500);
    } catch (err) {
      setError(err.message || 'Failed to update access settings');
    } finally {
      setAccessSaving(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Tab Effect
  useEffect(() => {
    if (activeTab === 'approvals') {
      loadApprovalsTab();
    } else if (activeTab === 'teachers') {
      loadAllTeachers();
    } else if (activeTab === 'school-admins') {
      loadSchoolAdmins();
      loadSchoolsTab();
    } else if (activeTab === 'schools') {
      loadSchoolsTab();
    } else if (activeTab === 'departments') { 
      loadDepartments(); 
      loadSchoolsTab(); 
    } else if (activeTab === 'courses') { 
      loadCourses(); 
      loadDepartments(); 
    } else if (activeTab === 'years') { 
      loadSchoolsTab(); 
      loadDepartments(); 
    } else if (activeTab === 'feedback') {
      loadFeedback();
    } else if (activeTab === 'settings') {
      loadAccessManagedUsers();
    }
  }, [activeTab, searchQuery, accessSearch, accessPage]);

  // --- ACTIONS ---
  const handleLogout = async () => {
    try { 
      await authAPI.logout(); 
    } catch (err) {
      console.error('Logout error:', err);
    } finally { 
      localStorage.removeItem('token'); 
      localStorage.removeItem('user'); 
      navigate('/'); 
    }
  };

  const handleApprove = async (id, permittedDepts, primaryDept) => {
    try { 
      await adminAPI.approveTeacher(id, { 
        permittedDepartments: permittedDepts || [],
        primaryDepartment: primaryDept || ''
      }); 
      setSuccess('Teacher approved successfully!'); 
      setPendingTeachers(prev => prev.filter(t => t._id !== id));
      setApprovalPanels(prev => { const n = {...prev}; delete n[id]; return n; });
      setApprovalPrimaryDepts(prev => { const n = {...prev}; delete n[id]; return n; });
      loadDashboardData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) { 
      setError(e.message || 'Failed to approve teacher'); 
    }
  };

  const handleReject = async (id, teacherName = 'this teacher') => {
    const reason = prompt(`Please enter rejection reason for ${teacherName}:`);
    
    if (!reason || reason.trim() === '') {
      setError('Rejection reason is required');
      return;
    }
    
    if (!confirm(`Are you sure you want to reject ${teacherName}? This action cannot be undone.`)) return;
    
    try { 
      await adminAPI.rejectTeacher(id, { reason: reason.trim() }); 
      setSuccess('Teacher rejected successfully!'); 
      setPendingTeachers(prev => prev.filter(t => t._id !== id));
      loadDashboardData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) { 
      setError(e.message || 'Failed to reject teacher'); 
    }
  };

  const handleToggleRestriction = async (id, currentStatus, teacherName) => {
    const newStatus = !currentStatus;
    const action = newStatus ? 'restrict' : 'unrestrict';
    
    if (!confirm(`Are you sure you want to ${action} upload access for ${teacherName}?`)) return;
    
    try {
      await adminAPI.toggleUserRestriction(id, newStatus);
      setAllTeachers(prev => prev.map(t => t._id === id ? { ...t, isRestricted: newStatus } : t));
      setSuccess(`Teacher ${action}ed successfully!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.message || `Failed to ${action} teacher`);
    }
  };

  const handleDeleteTeacher = async (id, teacherName = 'this teacher') => { 
    if(!confirm(`Are you sure you want to delete ${teacherName}? This action cannot be undone.`)) return;
    
    try { 
      await adminAPI.deleteTeacher(id); 
      setAllTeachers(prev => prev.filter(t => t._id !== id));
      setSuccess('Teacher deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.message || 'Failed to delete teacher');
    }
  };

  const handleDeleteSchool = async (id, schoolName = 'this school') => { 
    if(!confirm(`Are you sure you want to delete ${schoolName}?`)) return;
    
    try { 
      await adminAPI.deleteSchool(id); 
      setSchools(prev => prev.filter(s => s.id !== id)); 
      setSuccess('School deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.message || 'Failed to delete school');
    }
  };

  const handleDeleteDepartment = async (id, deptName = 'this department') => { 
    if(!confirm(`Are you sure you want to delete ${deptName}?`)) return;
    
    try { 
      await adminAPI.deleteDepartment(id); 
      setDepartments(prev => prev.filter(d => d._id !== id)); 
      setSuccess('Department deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.message || 'Failed to delete department');
    }
  };

  const handleDeleteCourse = async (id, courseName = 'this course') => { 
    if(!confirm(`Are you sure you want to delete ${courseName}?`)) return;
    
    try { 
      await adminAPI.deleteCourse(id); 
      setCourses(prev => prev.filter(c => c._id !== id)); 
      setSuccess('Course deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.message || 'Failed to delete course');
    }
  };

  const handleUpdateFeedbackStatus = async (id, status) => {
    try {
      await adminAPI.updateFeedbackStatus(id, status);
      setFeedback(prev => prev.map(f => f._id === id ? { ...f, status } : f));
      setSuccess('Feedback status updated!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.message || 'Failed to update feedback status');
    }
  };

  const handleDeleteFeedback = async (id) => {
    if (!confirm('Delete this feedback message?')) return;
    try {
      await adminAPI.deleteFeedback(id);
      setFeedback(prev => prev.filter(f => f._id !== id));
      setSuccess('Feedback deleted!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.message || 'Failed to delete feedback');
    }
  };

  // School Admin Management Handlers
 const handleCreateSchoolAdmin = async (e) => {
  e.preventDefault();
  try {
    const response = await adminAPI.createSchoolAdmin(schoolAdminForm);
    console.log('Create response:', response); // Debug
    
    setShowSchoolAdminForm(false);
    
    // Reload the school admins list
    await loadSchoolAdmins();
    await loadSchoolsTab();
    
    // Show password modal with credentials
    if (response.data && response.data.tempPassword) {
      setShowPasswordModal({
        name: schoolAdminForm.fullName,
        email: schoolAdminForm.email,
        password: response.data.tempPassword,
        action: 'created'
      });
    } else {
      // Fallback - show error
      setError('Admin created but no password returned');
    }
    
    setSuccess('School admin created successfully!');
    setTimeout(() => setSuccess(''), 3000);
    
    // Reset form
    setSchoolAdminForm({
      fullName: '',
      email: '',
      school: '',
      employeeId: ''
    });
  } catch (e) {
    console.error('Create school admin error:', e);
    setError(e.message || 'Failed to create school admin');
  }
};

  const handleUpdateSchoolAdmin = async (e) => {
    e.preventDefault();
    try {
      await adminAPI.updateSchoolAdmin(editingSchoolAdmin._id, schoolAdminForm);
      setShowSchoolAdminForm(false);
      loadSchoolAdmins();
      setSuccess('School admin updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
      setEditingSchoolAdmin(null);
      setSchoolAdminForm({
        fullName: '',
        email: '',
        school: '',
        employeeId: ''
      });
    } catch (e) {
      setError(e.message || 'Failed to update school admin');
    }
  };

  const handleDeleteSchoolAdmin = async (id, name) => {
    if (!confirm(`Are you sure you want to delete ${name}? This action cannot be undone.`)) return;
    
    try {
      await adminAPI.deleteSchoolAdmin(id);
      setSchoolAdmins(prev => prev.filter(admin => admin._id !== id));
      setSuccess('School admin deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.message || 'Failed to delete school admin');
    }
  };
const handleResetSchoolAdminPassword = async (id, name, email) => {
  if (!confirm(`Reset password for ${name}?`)) return;
  
  try {
    const response = await adminAPI.resetSchoolAdminPassword(id);
    console.log('Reset response:', response); // Debug
    
    if (response.data && response.data.tempPassword) {
      setShowPasswordModal({
        name: name,
        email: email,
        password: response.data.tempPassword,
        action: 'reset'
      });
      setSuccess(`Password reset successfully!`);
    } else {
      setError('Password reset failed - no password returned');
    }
    setTimeout(() => setSuccess(''), 3000);
  } catch (e) {
    setError(e.message || 'Failed to reset password');
  }
};

  const handleEditSchoolAdmin = (admin) => {
    setEditingSchoolAdmin(admin);
    setSchoolAdminForm({
      fullName: admin.fullName || '',
      email: admin.email || '',
      school: admin.school?._id || admin.school || '',
      employeeId: admin.employeeId || ''
    });
    setShowSchoolAdminForm(true);
  };

  // --- CREATE / UPDATE HANDLERS ---
  const handleCreateSchool = async (e) => { 
    e.preventDefault(); 
    try {
      await adminAPI.createSchool(schoolForm); 
      setShowSchoolForm(false); 
      loadSchoolsTab();
      setSuccess('School created successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.message || 'Failed to create school');
    }
  };

  const handleUpdateSchool = async (e) => { 
    e.preventDefault(); 
    try {
      await adminAPI.updateSchool(editingItem.data.id, schoolForm); 
      setShowSchoolForm(false); 
      loadSchoolsTab();
      setSuccess('School updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.message || 'Failed to update school');
    }
  };

  const handleCreateDepartment = async (e) => { 
    e.preventDefault(); 
    try {
      await adminAPI.createDepartment(departmentForm); 
      setShowDepartmentForm(false); 
      loadDepartments();
      setSuccess('Department created successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.message || 'Failed to create department');
    }
  };

  const handleUpdateDepartment = async (e) => { 
    e.preventDefault(); 
    try {
      await adminAPI.updateDepartment(editingItem.data._id, departmentForm); 
      setShowDepartmentForm(false); 
      loadDepartments();
      setSuccess('Department updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.message || 'Failed to update department');
    }
  };

  const handleCreateCourse = async (e) => { 
    e.preventDefault(); 
    try {
      await adminAPI.createCourse(courseForm); 
      setShowCourseForm(false); 
      loadCourses();
      setSuccess('Course created successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.message || 'Failed to create course');
    }
  };

  const handleUpdateCourse = async (e) => { 
    e.preventDefault(); 
    try {
      await adminAPI.updateCourse(editingItem.data._id, courseForm); 
      setShowCourseForm(false); 
      loadCourses();
      setSuccess('Course updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.message || 'Failed to update course');
    }
  };
  
  // --- EDIT PREP ---
  const handleEditSchool = (school) => { 
    setEditingItem({ type: 'school', data: school }); 
    setSchoolForm({ 
      name: school.name || '',
      code: school.code || '',
      description: school.description || '',
      contactEmail: school.contactEmail || '',
      contactPhone: school.contactPhone || '',
      dean: school.dean?._id || school.dean || ''
    }); 
    setShowSchoolForm(true); 
  };

  const handleEditDepartment = (dept) => { 
    setEditingItem({ type: 'department', data: dept }); 
    setDepartmentForm({ 
      name: dept.name || '',
      code: dept.code || '',
      school: dept.school?._id || dept.school || '',
      headOfDepartment: dept.headOfDepartment?._id || dept.headOfDepartment || '',
      description: dept.description || '',
      contactEmail: dept.contactEmail || ''
    }); 
    setShowDepartmentForm(true); 
  };

  const handleEditCourse = (c) => { 
    setEditingItem({ type: 'course', data: c }); 
    setCourseForm({ 
      title: c.title || '',
      courseCode: c.courseCode || '',
      department: c.department?._id || c.department || '',
      description: c.description || '',
      creditHours: c.credits || 3,
      semester: c.semester?.toString() || '1',
      year: c.level?.toString() || '1'
    }); 
    setShowCourseForm(true); 
  };
  
  const clearForm = () => {
    setShowSchoolForm(false); 
    setShowDepartmentForm(false); 
    setShowCourseForm(false);
    setShowSchoolAdminForm(false);
    setEditingItem(null); 
    setEditingSchoolAdmin(null);
    setYearManageDept(null); 
    setYearManageSchool('');
  };

  // ====== YEAR MANAGEMENT HANDLERS ======
  const handleSelectYearDept = (deptId) => {
    const dept = departments.find(d => d._id === deptId);
    setYearManageDept(dept);
  };

  const handleAddYear = async () => {
    if (!newYearInput || !yearManageDept) return;
    
    try {
      setLoading(true);
      await adminAPI.addDepartmentYear(yearManageDept._id, newYearInput);
      setSuccess(`Year ${newYearInput} added successfully!`);
      
      const updatedDepts = departments.map(d => {
        if (d._id === yearManageDept._id) {
          const updatedYears = [...(d.activeYears || []), parseInt(newYearInput)].sort((a,b) => a-b);
          d.activeYears = [...new Set(updatedYears)];
          return d;
        }
        return d;
      });
      setDepartments(updatedDepts);
      setYearManageDept(updatedDepts.find(d => d._id === yearManageDept._id));
      setNewYearInput('');
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to add year: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteYear = async (year) => {
    if (!confirm(`Remove Year ${year}?`)) return;
    
    try {
      setLoading(true);
      await adminAPI.removeDepartmentYear(yearManageDept._id, year);
      setSuccess(`Year ${year} removed successfully!`);
      
      const updatedDepts = departments.map(d => {
        if (d._id === yearManageDept._id) {
          d.activeYears = d.activeYears.filter(y => y !== year);
          return d;
        }
        return d;
      });
      setDepartments(updatedDepts);
      setYearManageDept(updatedDepts.find(d => d._id === yearManageDept._id));
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to remove year: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };


  if (!admin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Header />
      <div className="container mx-auto px-4 py-8">
        
        {/* Messages */}
        {error && (
          <div className="mb-6 bg-red-50 text-red-800 p-4 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="flex-1">{error}</span>
            <button 
              onClick={() => setError('')} 
              className="text-red-600 hover:text-red-800 font-medium"
            >
              Dismiss
            </button>
          </div>
        )}
        
        {success && (
          <div className="mb-6 bg-green-50 text-green-800 p-4 rounded-lg flex items-center gap-3">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <span className="flex-1">{success}</span>
            <button 
              onClick={() => setSuccess('')} 
              className="text-green-600 hover:text-green-800 font-medium"
            >
              Dismiss
            </button>
          </div>
        )}

{showPasswordModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-xl max-w-md w-full p-6">
      <div className="text-center mb-4">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">
          School Admin {showPasswordModal.action === 'created' ? 'Created' : 'Password Reset'}
        </h3>
      </div>
      
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
        <p className="text-sm text-yellow-800 mb-2 font-medium">
          ⚠️ Important: Save these credentials now!
        </p>
        <div className="space-y-2">
          <div>
            <label className="text-xs text-gray-600">Name:</label>
            <p className="font-medium">{showPasswordModal.name}</p>
          </div>
          <div>
            <label className="text-xs text-gray-600">Email:</label>
            <p className="font-medium">{showPasswordModal.email}</p>
          </div>
          <div>
            <label className="text-xs text-gray-600">Password:</label>
            <div className="bg-gray-100 p-2 rounded font-mono text-lg font-bold text-center">
              {showPasswordModal.password}
            </div>
          </div>
        </div>
      </div>
      
      <div className="flex gap-3">
        <button
          onClick={() => {
            navigator.clipboard.writeText(showPasswordModal.password);
            alert('Password copied to clipboard!');
          }}
          className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
        >
          Copy Password
        </button>
        <button
          onClick={() => setShowPasswordModal(null)}
          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          I've Saved It
        </button>
      </div>
    </div>
  </div>
)}
        <div className="mb-6">
          <Link to="/" className="text-blue-600 hover:text-blue-800 flex items-center gap-1">
            ← Back to Home
          </Link>
        </div>

<div className="mb-6 flex justify-end">
          <button
            onClick={() => { 
              setActiveTab('school-admins');  // Switch to correct tab
              setShowSchoolAdminForm(true); 
              setEditingSchoolAdmin(null); 
              setSchoolAdminForm({
                fullName: '',
                email: '',
                school: '',
                employeeId: ''
              });
            }}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 shadow-md"
          >
            <UserPlus className="w-4 h-4" />
            Add School Admin
          </button>
        </div>
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-green-700 to-green-900 p-8 text-white flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-full">
                <Shield className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">Admin Portal</h1>
                <p className="text-sm opacity-80">{admin.fullName} • {admin.email}</p>
              </div>
            </div>
            <Button 
              variant="secondary" 
              size="small" 
              onClick={handleLogout}
              className="whitespace-nowrap"
            >
              Logout
            </Button>
          </div>

          <div className="border-b border-gray-200 overflow-x-auto">
            <nav className="flex">
              {[
                'dashboard', 
                'approvals', 
                'teachers', 
                'school-admins', 
                'schools', 
                'departments', 
                'courses', 
                'years',
                'feedback',
                'chat',
                'settings'
              ].map((tab) => (
                <button
                  key={tab}
                  onClick={() => { 
                    setActiveTab(tab); 
                    clearForm(); 
                    setSearchQuery('');
                    setError('');
                    setSuccess('');
                  }}
                  className={`capitalize flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap relative ${
                    activeTab === tab 
                      ? 'text-green-600 border-b-2 border-green-600 bg-green-50' 
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {tab === 'chat' ? 'Colleague Chat' : tab.charAt(0).toUpperCase() + tab.slice(1).replace('-', ' ')}
                  {tab === 'approvals' && pendingTeachers.length > 0 && (
                    <span className="ml-2 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] flex items-center justify-center">
                      {pendingTeachers.length}
                    </span>
                  )}
                  {tab === 'feedback' && feedback.filter(f => f.status === 'new').length > 0 && (
                    <span className="ml-2 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] flex items-center justify-center">
                      {feedback.filter(f => f.status === 'new').length}
                    </span>
                  )}
                  {tab === 'chat' && totalUnreadChat > 0 && (
                    <span className="ml-2 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] flex items-center justify-center animate-bounce">
                      {totalUnreadChat}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-8">
            {/* Search */}
            {activeTab !== 'dashboard' && activeTab !== 'years' && activeTab !== 'settings' && (
              <div className="mb-8 relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={`Search ${activeTab.replace('-', ' ')}...`}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  disabled={loading}
                />
              </div>
            )}

            {loading && (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
              </div>
            )}

            {/* DASHBOARD TAB */}
            {!loading && activeTab === 'dashboard' && (
              <div className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {stats.map((stat, i) => (
                    <div key={i} className="bg-gray-50 p-6 rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
                      <div className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</div>
                      <div className="text-gray-600 text-sm">{stat.label}</div>
                    </div>
                  ))}
                </div>
                
                {/* Pending Approvals Section */}
                <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
                  <div className="bg-gray-100 px-6 py-4 border-b">
                    <h3 className="font-bold text-gray-800 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-orange-500" />
                      Pending Teacher Approvals ({pendingTeachers.length})
                    </h3>
                  </div>
                  
                  <div className="p-6">
                    {pendingTeachers.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                        <p className="font-medium">No pending approvals</p>
                        <p className="text-sm">All teacher registration requests have been processed.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {pendingTeachers.map(teacher => {
                          const panelDepts = approvalPanels[teacher._id];
                          const isOpen = panelDepts !== undefined;
                          const teacherSchoolId = teacher.teacherProfile?.school?._id || teacher.teacherProfile?.school;
                          const schoolDepts = departments.filter(d => {
                            const deptSchoolId = d.school?._id || d.school;
                            return String(deptSchoolId) === String(teacherSchoolId);
                          });

                          return (
                            <div key={teacher._id} className="flex flex-col p-4 border rounded-xl bg-orange-50/30 border-orange-100 transition-all gap-4">
                              <div className="flex flex-col md:flex-row md:items-center justify-between">
                                <div className="mb-3 md:mb-0">
                                  <div className="font-semibold text-gray-900">{teacher.fullName}</div>
                                  <div className="text-sm text-gray-600 mt-1 flex flex-wrap gap-x-3 gap-y-1 items-center">
                                    <span className="inline-flex items-center gap-1">
                                      <Mail className="w-3 h-3" />
                                      {teacher.email}
                                    </span>
                                    <span>•</span>
                                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                                      {teacher.teacherProfile?.school?.name || 'No school'}
                                    </span>
                                    <span>•</span>
                                    <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-100 rounded text-xs font-semibold">
                                      Requested Dept: {teacher.teacherProfile?.department?.name || 'N/A'}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <button 
                                    onClick={() => openApprovalPanel(teacher._id, teacher.teacherProfile?.department?._id || teacher.teacherProfile?.department)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${isOpen ? 'bg-green-600 text-white border-green-600' : 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'}`}
                                  >
                                    <CheckCircle className="w-4 h-4 inline mr-1" />
                                    {isOpen ? 'Assigning...' : 'Approve'}
                                  </button>
                                  <Button 
                                    variant="outline" 
                                    size="small" 
                                    onClick={() => handleReject(teacher._id, teacher.fullName)}
                                    className="border-red-300 text-red-600 hover:bg-red-50"
                                  >
                                    <XCircle className="w-4 h-4 mr-1" />
                                    Reject
                                  </Button>
                                </div>
                              </div>

                              {isOpen && (
                                <div className="bg-white p-5 rounded-xl border border-green-100 shadow-sm flex flex-col gap-4 animate-scale-up">
                                  <div>
                                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                                      Primary Department Assignment
                                    </p>
                                    <select
                                      value={approvalPrimaryDepts[teacher._id] || ''}
                                      onChange={(e) => setApprovalPrimaryDepts(prev => ({ ...prev, [teacher._id]: e.target.value }))}
                                      className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-green-500 bg-white"
                                    >
                                      <option value="">Select Primary Department</option>
                                      {schoolDepts.map(dept => (
                                        <option key={dept._id} value={dept._id}>{dept.name}</option>
                                      ))}
                                    </select>
                                  </div>

                                  <div>
                                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                                      Department Privileges (Permitted to Upload)
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                      {schoolDepts.map(dept => {
                                        const sel = panelDepts.includes(dept._id);
                                        return (
                                          <button
                                            key={dept._id}
                                            type="button"
                                            onClick={() => toggleApprovalDept(teacher._id, dept._id)}
                                            className={`px-3 py-1.5 rounded-lg text-[10px] font-bold border transition-all ${sel ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm' : 'bg-white text-gray-600 border-gray-200 hover:bg-indigo-50'}`}
                                          >
                                            {sel && '✓ '} {dept.name}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>

                                  <div className="flex justify-end gap-2 mt-2 pt-3 border-t border-slate-100">
                                    <button
                                      type="button"
                                      onClick={() => setApprovalPanels(prev => { const n = {...prev}; delete n[teacher._id]; return n; })}
                                      className="px-4 py-2 text-xs font-semibold text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 bg-white"
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleApprove(teacher._id, panelDepts, approvalPrimaryDepts[teacher._id])}
                                      className="px-4 py-2 text-xs font-bold text-white bg-green-600 rounded-lg hover:bg-green-700"
                                    >
                                      Confirm & Approve
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* APPROVALS TAB */}
            {!loading && activeTab === 'approvals' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold">Pending Approvals</h2>
                  <div className="text-sm text-gray-500">
                    Total: {pendingTeachers.length}
                  </div>
                </div>
                
                {pendingTeachers.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                    <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                    <p className="font-medium text-gray-700">No pending approvals</p>
                    <p className="text-sm text-gray-500 mt-1">All teacher registration requests have been processed</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b text-left text-gray-600 bg-gray-50">
                          <th className="py-3 px-4 font-medium">Name</th>
                          <th className="py-3 px-4 font-medium">Email</th>
                          <th className="py-3 px-4 font-medium">School</th>
                          <th className="py-3 px-4 font-medium">Employee ID</th>
                          <th className="py-3 px-4 font-medium">Applied Date</th>
                          <th className="py-3 px-4 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingTeachers.map(teacher => {
                          const panelDepts = approvalPanels[teacher._id];
                          const isOpen = panelDepts !== undefined;
                          const teacherSchoolId = teacher.teacherProfile?.school?._id || teacher.teacherProfile?.school;
                          const schoolDepts = departments.filter(d => {
                            const deptSchoolId = d.school?._id || d.school;
                            return String(deptSchoolId) === String(teacherSchoolId);
                          });

                          return (
                            <React.Fragment key={teacher._id}>
                              <tr className="border-b hover:bg-orange-50/10">
                                <td className="py-4 px-4 font-medium">
                                  <div>{teacher.fullName}</div>
                                  <div className="text-[10px] text-blue-600 font-bold uppercase tracking-wider mt-1 bg-blue-50 px-2 py-0.5 rounded inline-block border border-blue-100">Requested Dept: {teacher.teacherProfile?.department?.name || 'N/A'}</div>
                                </td>
                                <td className="py-4 px-4 text-gray-600">{teacher.email}</td>
                                <td className="py-4 px-4">
                                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm">
                                    {teacher.teacherProfile?.school?.name || 'N/A'}
                                  </span>
                                </td>
                                <td className="py-4 px-4 text-gray-600">{teacher.employeeId || 'N/A'}</td>
                                <td className="py-4 px-4 text-gray-500 text-sm">
                                  {teacher.createdAt ? new Date(teacher.createdAt).toLocaleDateString() : 'N/A'}
                                </td>
                                <td className="py-4 px-4">
                                  <div className="flex gap-2">
                                    <button
                                      type="button"
                                      onClick={() => openApprovalPanel(teacher._id, teacher.teacherProfile?.department?._id || teacher.teacherProfile?.department)}
                                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 border ${isOpen ? 'bg-green-600 text-white border-green-600' : 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'}`}
                                    >
                                      <CheckCircle className="w-4 h-4" />
                                      {isOpen ? 'Assigning...' : 'Approve'}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleReject(teacher._id, teacher.fullName)}
                                      className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors flex items-center gap-1"
                                    >
                                      <XCircle className="w-4 h-4" />
                                      Reject
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              {isOpen && (
                                <tr className="bg-green-50/40">
                                  <td colSpan={6} className="p-6">
                                    <div className="flex flex-col gap-6 max-w-4xl">
                                      {/* Primary Department Assignment Dropdown */}
                                      <div className="bg-white p-5 rounded-xl border border-green-100 shadow-sm">
                                        <p className="text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-2.5 flex items-center gap-1">
                                          <GraduationCap className="w-4 h-4 text-green-600" /> Assign Primary Department
                                        </p>
                                        <select
                                          value={approvalPrimaryDepts[teacher._id] || ''}
                                          onChange={(e) => setApprovalPrimaryDepts(prev => ({ ...prev, [teacher._id]: e.target.value }))}
                                          className="w-full p-3.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-green-500 bg-white"
                                        >
                                          <option value="">Select Primary Department</option>
                                          {schoolDepts.map(dept => (
                                            <option key={dept._id} value={dept._id}>{dept.name}</option>
                                          ))}
                                        </select>
                                        <p className="text-[9px] text-gray-400 font-semibold mt-1.5 uppercase tracking-wide">
                                          Note: This sets the teacher's main academic department affiliation.
                                        </p>
                                      </div>

                                      {/* Department Access Privileges */}
                                      <div className="bg-white p-5 rounded-xl border border-green-100 shadow-sm">
                                        <p className="text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-2.5 flex items-center gap-1">
                                          <GraduationCap className="w-4 h-4 text-green-600" /> Department Permissions (Access Privileges)
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                          {schoolDepts.map(dept => {
                                            const sel = panelDepts.includes(dept._id);
                                            return (
                                              <button
                                                key={dept._id}
                                                type="button"
                                                onClick={() => toggleApprovalDept(teacher._id, dept._id)}
                                                className={`px-3 py-1.5 rounded-lg text-[10px] font-bold border transition-all ${sel ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm' : 'bg-white text-gray-600 border-gray-200 hover:bg-indigo-50'}`}
                                              >
                                                {sel && '✓ '} {dept.name}
                                              </button>
                                            );
                                          })}
                                        </div>
                                        <p className={`text-[10px] font-bold mt-2 ${panelDepts.length === 0 ? 'text-red-500' : 'text-indigo-600'}`}>
                                          {panelDepts.length === 0 ? '⚠ No departments selected — teacher will have ZERO upload privileges' : `✓ Restricted access granted for ${panelDepts.length} department(s)`}
                                        </p>
                                      </div>

                                      <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                                        <button
                                          type="button"
                                          onClick={() => setApprovalPanels(prev => { const n = {...prev}; delete n[teacher._id]; return n; })}
                                          className="px-5 py-2.5 text-xs font-semibold text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 bg-white"
                                        >
                                          Cancel
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleApprove(teacher._id, panelDepts, approvalPrimaryDepts[teacher._id])}
                                          className="px-5 py-2.5 text-xs font-bold text-white bg-green-600 rounded-lg hover:bg-green-700 shadow-sm"
                                        >
                                          Confirm & Approve
                                        </button>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* TEACHERS TAB */}
            {!loading && activeTab === 'teachers' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <Users className="w-6 h-6" />
                    Manage Teachers
                  </h2>
                </div>
                
                {allTeachers.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="font-medium text-gray-700">No teachers found</p>
                    <p className="text-sm text-gray-500 mt-1">No active teachers in the system</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b text-left text-gray-600 bg-gray-50">
                          <th className="py-3 px-4 font-medium">Name</th>
                          <th className="py-3 px-4 font-medium">Email</th>
                          <th className="py-3 px-4 font-medium">School</th>
                          <th className="py-3 px-4 font-medium">Status</th>
                          <th className="py-3 px-4 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allTeachers.map(teacher => (
                          <tr key={teacher._id} className="border-b hover:bg-gray-50">
                            <td className="py-4 px-4 font-medium">{teacher.fullName}</td>
                            <td className="py-4 px-4 text-gray-600">{teacher.email}</td>
                            <td className="py-4 px-4">
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm">
                                {teacher.teacherProfile?.school?.name || 'N/A'}
                              </span>
                            </td>
                            <td className="py-4 px-4">
                              <span className={`px-2 py-1 rounded text-sm ${
                                teacher.isActive 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                {teacher.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="py-4 px-4">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleToggleRestriction(teacher._id, teacher.isRestricted, teacher.fullName)}
                                  className={`px-3 py-1 rounded text-sm transition-colors flex items-center gap-1 ${
                                    teacher.isRestricted 
                                      ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' 
                                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                  }`}
                                  title={teacher.isRestricted ? "Unrestrict Uploads" : "Restrict Uploads"}
                                >
                                  <Ban className="w-4 h-4" />
                                  {teacher.isRestricted ? 'Unrestrict' : 'Restrict'}
                                </button>
                                <button
                                  onClick={() => handleDeleteTeacher(teacher._id, teacher.fullName)}
                                  className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 flex items-center gap-1"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

           {/* SCHOOL ADMINS TAB */}
{!loading && activeTab === 'school-admins' && (
  <div>

    {/* School Admin Form */}
    {showSchoolAdminForm && (
      <div className="bg-gray-50 p-6 rounded-xl border mb-6">
        <h3 className="font-bold text-gray-800 mb-4">
          {editingSchoolAdmin ? 'Edit School Admin' : 'Add New School Admin'}
        </h3>
        <form onSubmit={editingSchoolAdmin ? handleUpdateSchoolAdmin : handleCreateSchoolAdmin}>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium mb-1">Full Name *</label>
              <input
                className="w-full p-2 border rounded"
                placeholder="John Doe"
                value={schoolAdminForm.fullName}
                onChange={e => setSchoolAdminForm({...schoolAdminForm, fullName: e.target.value})}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email *</label>
              <input
                type="email"
                className="w-full p-2 border rounded"
                placeholder="admin@school.edu"
                value={schoolAdminForm.email}
                onChange={e => setSchoolAdminForm({...schoolAdminForm, email: e.target.value})}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">School *</label>
              <select
                className="w-full p-2 border rounded bg-white"
                value={schoolAdminForm.school}
                onChange={e => setSchoolAdminForm({...schoolAdminForm, school: e.target.value})}
                required
              >
                <option value="">Select School</option>
                {schools.map(school => (
                  <option key={school.id} value={school.id}>
                    {school.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Employee ID</label>
              <input
                className="w-full p-2 border rounded"
                placeholder="EMP001"
                value={schoolAdminForm.employeeId}
                onChange={e => setSchoolAdminForm({...schoolAdminForm, employeeId: e.target.value})}
              />
            </div>
            {!editingSchoolAdmin && (
              <div>
                <label className="block text-sm font-medium mb-1">Password (Optional)</label>
                <input
                  type="password"
                  className="w-full p-2 border rounded"
                  placeholder="Leave blank for random"
                  value={schoolAdminForm.password || ''}
                  onChange={e => setSchoolAdminForm({...schoolAdminForm, password: e.target.value})}
                />
              </div>
            )}
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <button 
              type="button" 
              onClick={() => {
                setShowSchoolAdminForm(false);
                setEditingSchoolAdmin(null);
                setSchoolAdminForm({
                  fullName: '',
                  email: '',
                  school: '',
                  employeeId: ''
                });
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button 
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              {editingSchoolAdmin ? 'Update School Admin' : 'Create School Admin'}
            </button>
          </div>
        </form>
      </div>
    )}

    {/* School Admins List */}
    {schoolAdmins.length === 0 ? (
      <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
        <School className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="font-medium text-gray-700">No school admins found</p>
        <p className="text-sm text-gray-500 mt-1">Click "Add School Admin" to create one</p>
      </div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b text-left text-gray-600 bg-gray-50">
              <th className="py-3 px-4 font-medium">Name</th>
              <th className="py-3 px-4 font-medium">Email</th>
              <th className="py-3 px-4 font-medium">School</th>
              <th className="py-3 px-4 font-medium">Employee ID</th>
              <th className="py-3 px-4 font-medium">Status</th>
              <th className="py-3 px-4 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {schoolAdmins.map(admin => (
              <tr key={admin._id} className="border-b hover:bg-gray-50">
                <td className="py-4 px-4 font-medium">{admin.fullName}</td>
                <td className="py-4 px-4 text-gray-600">{admin.email}</td>
                <td className="py-4 px-4">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm">
                    {admin.school?.name || 'N/A'}
                  </span>
                </td>
                <td className="py-4 px-4 text-gray-600">{admin.employeeId || 'N/A'}</td>
                <td className="py-4 px-4">
                  <span className={`px-2 py-1 rounded text-sm ${
                    admin.isActive 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {admin.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="py-4 px-4">
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditSchoolAdmin(admin)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                      title="Edit Admin"
                    >
                      <Edit className="w-4 h-4"/>
                    </button>
                    <button
                      onClick={() => handleResetSchoolAdminPassword(admin._id, admin.fullName, admin.email)}
                      className="p-2 text-orange-600 hover:bg-orange-50 rounded"
                      title="Reset Password"
                    >
                      <RefreshCw className="w-4 h-4"/>
                    </button>
                    <button
                      onClick={() => handleDeleteSchoolAdmin(admin._id, admin.fullName)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                      title="Delete Admin"
                    >
                      <Trash2 className="w-4 h-4"/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
)}

            {/* SCHOOLS TAB */}
            {!loading && activeTab === 'schools' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <Building className="w-6 h-6" />
                    Manage Schools
                  </h2>
                   {/* Show error if any */}
    {error && (
      <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg">
        {error}
      </div>
    )}
                  <Button 
                    variant="primary" 
                    icon={Plus} 
                    onClick={() => { 
                      setShowSchoolForm(true); 
                      setEditingItem(null); 
                      setSchoolForm({
                        name: '', 
                        code: '', 
                        description: '', 
                        contactEmail: '', 
                        contactPhone: '', 
                        dean: ''
                      });
                    }}
                  >
                    Add School
                  </Button>
                </div>

                {/* School Form */}
                {showSchoolForm && (
                  <div className="bg-gray-50 p-6 rounded-xl border mb-6">
                    <h3 className="font-bold text-gray-800 mb-4">
                      {editingItem ? 'Edit School' : 'Add New School'}
                    </h3>
                    <form onSubmit={editingItem ? handleUpdateSchool : handleCreateSchool}>
                      <div className="grid gap-4 md:grid-cols-2">
                        <div>
                          <label className="block text-sm font-medium mb-1">Name *</label>
                          <input
                            className="w-full p-2 border rounded"
                            placeholder="School Name"
                            value={schoolForm.name}
                            onChange={e => setSchoolForm({...schoolForm, name: e.target.value})}
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Code *</label>
                          <input
                            className="w-full p-2 border rounded"
                            placeholder="SCHOOL_CODE"
                            value={schoolForm.code}
                            onChange={e => setSchoolForm({...schoolForm, code: e.target.value.toUpperCase()})}
                            required
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium mb-1">Description</label>
                          <textarea
                            className="w-full p-2 border rounded"
                            placeholder="School description..."
                            value={schoolForm.description}
                            onChange={e => setSchoolForm({...schoolForm, description: e.target.value})}
                            rows={3}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Contact Email</label>
                          <input
                            type="email"
                            className="w-full p-2 border rounded"
                            placeholder="contact@school.edu"
                            value={schoolForm.contactEmail}
                            onChange={e => setSchoolForm({...schoolForm, contactEmail: e.target.value})}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Contact Phone</label>
                          <input
                            className="w-full p-2 border rounded"
                            placeholder="+1234567890"
                            value={schoolForm.contactPhone}
                            onChange={e => setSchoolForm({...schoolForm, contactPhone: e.target.value})}
                          />
                        </div>
                      </div>
                      <div className="mt-6 flex justify-end gap-2">
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => setShowSchoolForm(false)}
                        >
                          Cancel
                        </Button>
                        <Button type="submit">
                          {editingItem ? 'Update School' : 'Create School'}
                        </Button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Schools List */}
                {schools.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                    <Building className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="font-medium text-gray-700">No schools found</p>
                    <p className="text-sm text-gray-500 mt-1">Add your first school to get started</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {schools.map(school => (
                      <div key={school.id} className="p-4 border rounded-xl hover:shadow-md bg-white">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h3 className="font-bold text-lg">{school.name}</h3>
                            <p className="text-sm text-gray-500">{school.code}</p>
                          </div>
                          <div className="flex gap-1">
                            <button 
                              onClick={() => handleEditSchool(school)} 
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                              title="Edit School"
                            >
                              <Edit className="w-4 h-4"/>
                            </button>
                            <button 
                              onClick={() => handleDeleteSchool(school.id, school.name)} 
                              className="p-2 text-red-600 hover:bg-red-50 rounded"
                              title="Delete School"
                            >
                              <Trash2 className="w-4 h-4"/>
                            </button>
                          </div>
                        </div>
                        <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                          {school.description || 'No description provided'}
                        </p>
                        <div className="flex items-center justify-between text-sm text-gray-500">
                          <span>{school.departments} departments</span>
                          <span className={`px-2 py-1 rounded ${
                            school.status === 'active' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {school.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* DEPARTMENTS TAB */}
            {!loading && activeTab === 'departments' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <GraduationCap className="w-6 h-6" />
                    Manage Departments
                  </h2>
                  <Button 
                    variant="primary" 
                    icon={Plus} 
                    onClick={() => { 
                      setShowDepartmentForm(true); 
                      setEditingItem(null); 
                      setDepartmentForm({
                        name: '', 
                        code: '', 
                        school: '', 
                        headOfDepartment: '', 
                        description: '', 
                        contactEmail: ''
                      });
                    }}
                  >
                    Add Department
                  </Button>
                </div>

                {/* Department Form */}
                {showDepartmentForm && (
                  <div className="bg-gray-50 p-6 rounded-xl border mb-6">
                    <h3 className="font-bold text-gray-800 mb-4">
                      {editingItem ? 'Edit Department' : 'Add New Department'}
                    </h3>
                    <form onSubmit={editingItem ? handleUpdateDepartment : handleCreateDepartment}>
                      <div className="grid gap-4 md:grid-cols-2">
                        <div>
                          <label className="block text-sm font-medium mb-1">Name *</label>
                          <input
                            className="w-full p-2 border rounded"
                            placeholder="Department Name"
                            value={departmentForm.name}
                            onChange={e => setDepartmentForm({...departmentForm, name: e.target.value})}
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Code *</label>
                          <input
                            className="w-full p-2 border rounded"
                            placeholder="DEPT_CODE"
                            value={departmentForm.code}
                            onChange={e => setDepartmentForm({...departmentForm, code: e.target.value.toUpperCase()})}
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">School *</label>
                          <select
                            className="w-full p-2 border rounded bg-white"
                            value={departmentForm.school}
                            onChange={e => setDepartmentForm({...departmentForm, school: e.target.value})}
                            required
                          >
                            <option value="">Select School</option>
                            {schools.map(school => (
                              <option key={school.id} value={school.id}>
                                {school.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium mb-1">Description</label>
                          <textarea
                            className="w-full p-2 border rounded"
                            placeholder="Department description..."
                            value={departmentForm.description}
                            onChange={e => setDepartmentForm({...departmentForm, description: e.target.value})}
                            rows={3}
                          />
                        </div>
                      </div>
                      <div className="mt-6 flex justify-end gap-2">
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => setShowDepartmentForm(false)}
                        >
                          Cancel
                        </Button>
                        <Button type="submit">
                          {editingItem ? 'Update Department' : 'Create Department'}
                        </Button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Departments List */}
                {departments.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                    <GraduationCap className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="font-medium text-gray-700">No departments found</p>
                    <p className="text-sm text-gray-500 mt-1">Add your first department to get started</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b text-left text-gray-600 bg-gray-50">
                          <th className="py-3 px-4 font-medium">Name</th>
                          <th className="py-3 px-4 font-medium">Code</th>
                          <th className="py-3 px-4 font-medium">School</th>
                          <th className="py-3 px-4 font-medium">Active Years</th>
                          <th className="py-3 px-4 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {departments.map(dept => (
                          <tr key={dept._id} className="border-b hover:bg-gray-50">
                            <td className="py-4 px-4 font-medium">{dept.name}</td>
                            <td className="py-4 px-4">{dept.code}</td>
                            <td className="py-4 px-4">
                              {dept.school?.name || 'Unknown School'}
                            </td>
                            <td className="py-4 px-4">
                              <div className="flex flex-wrap gap-1">
                                {dept.activeYears && dept.activeYears.length > 0 ? (
                                  dept.activeYears.sort((a, b) => a - b).map(year => (
                                    <span 
                                      key={year} 
                                      className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs"
                                    >
                                      Year {year}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-gray-400 text-sm">No years set</span>
                                )}
                              </div>
                            </td>
                            <td className="py-4 px-4">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleEditDepartment(dept)}
                                  className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                                  title="Edit Department"
                                >
                                  <Edit className="w-4 h-4"/>
                                </button>
                                <button
                                  onClick={() => handleDeleteDepartment(dept._id, dept.name)}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded"
                                  title="Delete Department"
                                >
                                  <Trash2 className="w-4 h-4"/>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* COURSES TAB */}
            {!loading && activeTab === 'courses' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <BookOpen className="w-6 h-6" />
                    Manage Courses
                  </h2>
                  <Button 
                    variant="primary" 
                    icon={Plus} 
                    onClick={() => { 
                      setShowCourseForm(true); 
                      setEditingItem(null); 
                      setCourseForm({
                        title: '', 
                        courseCode: '', 
                        department: '', 
                        description: '', 
                        creditHours: 3, 
                        semester: '1', 
                        year: '1'
                      });
                    }}
                  >
                    Add Course
                  </Button>
                </div>

                {/* Course Form */}
                {showCourseForm && (
                  <div className="bg-gray-50 p-6 rounded-xl border mb-6">
                    <h3 className="font-bold text-gray-800 mb-4">
                      {editingItem ? 'Edit Course' : 'Add New Course'}
                    </h3>
                    <form onSubmit={editingItem ? handleUpdateCourse : handleCreateCourse}>
                      <div className="grid gap-4 md:grid-cols-2">
                        <div>
                          <label className="block text-sm font-medium mb-1">Title *</label>
                          <input
                            className="w-full p-2 border rounded"
                            placeholder="Course Title"
                            value={courseForm.title}
                            onChange={e => setCourseForm({...courseForm, title: e.target.value})}
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Course Code *</label>
                          <input
                            className="w-full p-2 border rounded"
                            placeholder="CS101"
                            value={courseForm.courseCode}
                            onChange={e => setCourseForm({...courseForm, courseCode: e.target.value})}
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Department *</label>
                          <select
                            className="w-full p-2 border rounded bg-white"
                            value={courseForm.department}
                            onChange={e => setCourseForm({...courseForm, department: e.target.value})}
                            required
                          >
                            <option value="">Select Department</option>
                            {departments.map(dept => (
                              <option key={dept._id} value={dept._id}>
                                {dept.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Credits</label>
                          <input
                            type="number"
                            className="w-full p-2 border rounded"
                            placeholder="3"
                            value={courseForm.creditHours}
                            onChange={e => setCourseForm({...courseForm, creditHours: e.target.value})}
                            min="1"
                            max="6"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Year</label>
                          <select
                            className="w-full p-2 border rounded bg-white"
                            value={courseForm.year}
                            onChange={e => setCourseForm({...courseForm, year: e.target.value})}
                          >
                            {[1, 2, 3, 4, 5].map(year => (
                              <option key={year} value={year}>Year {year}</option>
                            ))}
                          </select>
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium mb-1">Description</label>
                          <textarea
                            className="w-full p-2 border rounded"
                            placeholder="Course description..."
                            value={courseForm.description}
                            onChange={e => setCourseForm({...courseForm, description: e.target.value})}
                            rows={3}
                          />
                        </div>
                      </div>
                      <div className="mt-6 flex justify-end gap-2">
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => setShowCourseForm(false)}
                        >
                          Cancel
                        </Button>
                        <Button type="submit">
                          {editingItem ? 'Update Course' : 'Create Course'}
                        </Button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Courses List */}
                {courses.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                    <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="font-medium text-gray-700">No courses found</p>
                    <p className="text-sm text-gray-500 mt-1">Add your first course to get started</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {courses.map(course => (
                      <div key={course._id} className="flex justify-between items-center p-4 border rounded-lg bg-white hover:shadow-sm">
                        <div>
                          <div className="font-bold text-gray-900">{course.title}</div>
                          <div className="text-sm text-gray-500 mt-1">
                            {course.courseCode} • {course.credits || 3} Credits • Year {course.level || 1}
                          </div>
                          <div className="text-sm text-gray-600 mt-2">
                            {course.department?.name || 'No department'}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditCourse(course)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                            title="Edit Course"
                          >
                            <Edit className="w-4 h-4"/>
                          </button>
                          <button
                            onClick={() => handleDeleteCourse(course._id, course.title)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                            title="Delete Course"
                          >
                            <Trash2 className="w-4 h-4"/>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* YEARS MANAGEMENT TAB */}
            {!loading && activeTab === 'years' && (
              <div>
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                  <Calendar className="w-6 h-6" />
                  Manage Department Years
                </h2>
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Selection Panel */}
                  <div className="space-y-4">
                    <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
                      <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                        1. Select School & Department
                      </h3>
                      <div className="mb-4">
                        <label className="block text-sm font-medium mb-1">School</label>
                        <select 
                          className="w-full p-2 border rounded bg-white"
                          value={yearManageSchool} 
                          onChange={(e) => { 
                            setYearManageSchool(e.target.value); 
                            setYearManageDept(null); 
                          }}
                        >
                          <option value="">Select a School</option>
                          {schools.map(school => (
                            <option key={school.id} value={school.id}>
                              {school.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Department</label>
                        <select 
                          className="w-full p-2 border rounded bg-white"
                          disabled={!yearManageSchool} 
                          value={yearManageDept?._id || ''} 
                          onChange={(e) => handleSelectYearDept(e.target.value)}
                        >
                          <option value="">Select a Department</option>
                          {departments
                            .filter(d => (d.school?._id || d.school) === yearManageSchool)
                            .map(d => (
                              <option key={d._id} value={d._id}>
                                {d.name}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>

                    {yearManageDept && (
                      <div className="bg-green-50 p-6 rounded-xl border border-green-200">
                        <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                          2. Add New Year
                        </h3>
                        <div className="flex gap-2">
                          <input 
                            type="number" 
                            min="1" 
                            max="7" 
                            className="flex-1 p-2 border rounded"
                            placeholder="e.g. 5" 
                            value={newYearInput} 
                            onChange={(e) => setNewYearInput(e.target.value)}
                          />
                          <Button 
                            variant="primary" 
                            onClick={handleAddYear}
                            disabled={!newYearInput || loading}
                          >
                            Add Year
                          </Button>
                        </div>
                        <p className="text-sm text-gray-500 mt-2">
                          Add years 1-7 that this department offers courses for
                        </p>
                      </div>
                    )}
                  </div>

                  {/* List Panel */}
                  <div>
                    {yearManageDept ? (
                      <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
                        <div className="bg-gray-100 px-6 py-4 border-b">
                          <h3 className="font-bold text-gray-800">
                            {yearManageDept.name} - Active Years
                          </h3>
                        </div>
                        <div className="p-6">
                          {(!yearManageDept.activeYears || yearManageDept.activeYears.length === 0) ? (
                            <div className="text-center py-8 text-gray-500">
                              <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                              <p className="font-medium">No active years configured</p>
                              <p className="text-sm">Add years using the form on the left</p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {yearManageDept.activeYears
                                .sort((a, b) => a - b)
                                .map(year => (
                                  <div key={year} className="flex justify-between items-center p-4 border rounded-lg hover:bg-gray-50">
                                    <div className="flex items-center gap-3">
                                      <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center">
                                        <span className="font-bold">{year}</span>
                                      </div>
                                      <div>
                                        <div className="font-medium text-gray-700">Year {year}</div>
                                        <div className="text-sm text-gray-500">
                                          Available for course assignment
                                        </div>
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => handleDeleteYear(year)}
                                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                      title="Remove Year"
                                      disabled={loading}
                                    >
                                      <Trash2 className="w-5 h-5" />
                                    </button>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="h-full flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl p-8 text-gray-400">
                        <Calendar className="w-16 h-16 mb-4" />
                        <p className="font-medium mb-1">Select a department</p>
                        <p className="text-sm">Choose a school and department to manage years</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* FEEDBACK TAB */}
            {!loading && activeTab === 'feedback' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                  <div>
                    <h2 className="text-2xl font-bold flex items-center gap-2">
                      <Mail className="w-6 h-6 text-blue-600" />
                      User Feedback
                    </h2>
                    <p className="text-gray-500 text-sm">Review suggestions and reports from university users</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-sm font-medium px-4 py-2 bg-blue-50 text-blue-700 rounded-lg">
                      Total: {feedback.length}
                    </div>
                  </div>
                </div>

                {feedback.length === 0 ? (
                  <div className="text-center py-20 bg-white rounded-2xl border-2 border-dashed border-gray-200 shadow-sm">
                    <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                      <MessageSquare className="w-10 h-10 text-gray-300" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">No feedback yet</h3>
                    <p className="text-gray-500">When users send messages, they will appear here.</p>
                  </div>
                ) : (
                  <div className="grid gap-6">
                    {feedback.map((msg) => (
                      <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        key={msg._id} 
                        className={`bg-white rounded-2xl shadow-sm border-l-4 overflow-hidden shadow-sm hover:shadow-md transition-shadow ${
                          msg.status === 'new' ? 'border-l-blue-600' : 'border-l-gray-300'
                        }`}
                      >
                        <div className="p-6">
                          <div className="flex flex-col md:flex-row justify-between gap-4 mb-6">
                            <div className="flex gap-4">
                              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center font-bold text-gray-600 flex-shrink-0">
                                {msg.fullName?.[0]?.toUpperCase() || 'U'}
                              </div>
                              <div>
                                <h3 className="text-lg font-bold text-gray-900">{msg.fullName}</h3>
                                <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                                  <span className="flex items-center gap-1">
                                    <Mail className="w-3 h-3" /> {msg.email}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" /> {new Date(msg.createdAt).toLocaleDateString()}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <select 
                                value={msg.status}
                                onChange={(e) => handleUpdateFeedbackStatus(msg._id, e.target.value)}
                                className={`text-xs font-bold px-3 py-1.5 rounded-full border-0 focus:ring-0 cursor-pointer ${
                                  msg.status === 'new' 
                                    ? 'bg-blue-100 text-blue-700' 
                                    : msg.status === 'read' 
                                      ? 'bg-green-100 text-green-700' 
                                      : 'bg-gray-100 text-gray-700'
                                }`}
                              >
                                <option value="new">New</option>
                                <option value="read">Mark Read</option>
                                <option value="archived">Archive</option>
                              </select>
                              <button 
                                onClick={() => handleDeleteFeedback(msg._id)}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          
                          <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                              <div className="text-xs font-bold text-blue-600 uppercase tracking-wider flex items-center gap-2">
                                <Tag className="w-3 h-3" /> {msg.subject}
                              </div>
                            <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                              {msg.message}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* SETTINGS */}
            {!loading && activeTab === 'settings' && (
              <div className="space-y-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Access Management</h2>
                    <p className="mt-1 text-sm text-gray-500">Manage account roles, feature permissions, and upload access.</p>
                  </div>
                  <div className="relative w-full sm:max-w-sm">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type="search"
                      value={accessSearch}
                      onChange={event => { setAccessSearch(event.target.value); setAccessPage(1); }}
                      placeholder="Search users by name or email"
                      className="w-full rounded-lg border border-gray-300 py-2.5 pl-9 pr-3 text-sm focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-100"
                    />
                  </div>
                </div>

                <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
                  <table className="min-w-[1450px] w-full text-left text-sm">
                    <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                      <tr>
                        <th className="px-4 py-3 font-semibold">User</th>
                        <th className="px-4 py-3 font-semibold">Role</th>
                        <th className="px-4 py-3 font-semibold">Account</th>
                        <th className="px-4 py-3 font-semibold">Sensitive Data</th>
                        <th className="px-4 py-3 font-semibold">Schools</th>
                        <th className="px-4 py-3 font-semibold">School Admins</th>
                        <th className="px-4 py-3 font-semibold">College Chat</th>
                        <th className="px-4 py-3 font-semibold">Personal Files</th>
                        <th className="px-4 py-3 font-semibold">Teacher Uploads</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {accessLoading ? (
                        <tr><td colSpan={9} className="px-4 py-12 text-center text-gray-500">Loading access records...</td></tr>
                      ) : accessUsers.length === 0 ? (
                        <tr><td colSpan={9} className="px-4 py-12 text-center text-gray-500">No users found.</td></tr>
                      ) : accessUsers.map(user => {
                        const isCurrentAdmin = user._id === admin?._id;
                        const isSaving = Boolean(accessSaving[user._id]);
                        return (
                          <tr key={user._id} className="align-middle hover:bg-gray-50/70">
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900">{user.fullName}</div>
                              <div className="text-xs text-gray-500">{user.email}</div>
                              {user.school?.name && <div className="mt-1 text-xs text-gray-400">{user.school.name}</div>}
                            </td>
                            <td className="px-4 py-3">
                              <select
                                aria-label={`Role for ${user.fullName}`}
                                value={user.role}
                                disabled={isCurrentAdmin || isSaving}
                                onChange={event => updateAccess(user._id, { role: event.target.value })}
                                className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm disabled:cursor-not-allowed disabled:bg-gray-100"
                              >
                                {['student', 'teacher', 'pending_teacher', 'school_admin', 'admin'].map(role => (
                                  <option key={role} value={role}>{role.replace('_', ' ')}</option>
                                ))}
                              </select>
                            </td>
                            <td className="px-4 py-3">
                              <label className="inline-flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={user.isActive}
                                  disabled={isCurrentAdmin || isSaving}
                                  onChange={event => updateAccess(user._id, { isActive: event.target.checked })}
                                  className="h-4 w-4 accent-green-700"
                                />
                                <span className={user.isActive ? 'text-green-700' : 'text-red-600'}>{user.isActive ? 'Active' : 'Disabled'}</span>
                              </label>
                            </td>
                            {[
                              ['canManageSensitiveData', 'Sensitive data'],
                              ['canManageSchools', 'Schools'],
                              ['canManageSchoolAdmins', 'School admins']
                            ].map(([permission, label]) => (
                              <td key={permission} className="px-4 py-3">
                                {(() => {
                                  const isAdminOnlyPermission = user.role !== 'admin';
                                  const isGranted = !isAdminOnlyPermission && user.permissions?.[permission] !== false;
                                  return (
                                <label className="inline-flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={isGranted}
                                    disabled={isSaving || isAdminOnlyPermission || isCurrentAdmin}
                                    onChange={event => updateAccess(user._id, { permissions: { [permission]: event.target.checked } })}
                                    className="h-4 w-4 accent-green-700"
                                  />
                                  <span className="text-gray-600">{isAdminOnlyPermission ? 'Admin only' : isGranted ? 'Granted' : 'Denied'}</span>
                                </label>
                                  );
                                })()}
                                <span className="sr-only">{label}</span>
                              </td>
                            ))}
                            {['canUseCollegeChat', 'canUsePersonalFile'].map(permission => (
                              <td key={permission} className="px-4 py-3">
                                <label className="inline-flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={user.permissions?.[permission] !== false}
                                    disabled={isSaving}
                                    onChange={event => updateAccess(user._id, { permissions: { [permission]: event.target.checked } })}
                                    className="h-4 w-4 accent-green-700"
                                  />
                                  <span className="text-gray-600">{user.permissions?.[permission] === false ? 'Denied' : 'Granted'}</span>
                                </label>
                              </td>
                            ))}
                            <td className="px-4 py-3">
                              {user.role === 'teacher' ? (
                                <label className="inline-flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={!user.isRestricted}
                                    disabled={isSaving}
                                    onChange={event => updateAccess(user._id, { isRestricted: !event.target.checked })}
                                    className="h-4 w-4 accent-green-700"
                                  />
                                  <span className="text-gray-600">{user.isRestricted ? 'Denied' : 'Granted'}</span>
                                </label>
                              ) : <span className="text-gray-400">Not applicable</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>{accessTotal} users</span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setAccessPage(page => Math.max(1, page - 1))}
                      disabled={accessPage <= 1 || accessLoading}
                      className="rounded-md border border-gray-300 px-3 py-1.5 disabled:opacity-40"
                    >Previous</button>
                    <span>Page {accessPage} of {Math.max(1, Math.ceil(accessTotal / 25))}</span>
                    <button
                      type="button"
                      onClick={() => setAccessPage(page => page + 1)}
                      disabled={accessPage >= Math.ceil(accessTotal / 25) || accessLoading}
                      className="rounded-md border border-gray-300 px-3 py-1.5 disabled:opacity-40"
                    >Next</button>
                  </div>
                </div>
              </div>
            )}

            {/* CHAT / MESSAGING */}
            {!loading && activeTab === 'chat' && (
              <div className="bg-white rounded-3xl border border-slate-100 shadow-xl overflow-hidden h-[calc(100vh-230px)] md:h-[650px] min-h-[460px] max-h-[700px] flex animate-fade-in">
                {/* Contacts List Column */}
                <div className={`w-full md:w-[350px] border-r border-slate-100 flex flex-col h-full bg-slate-50/50 ${selectedContact ? 'hidden md:flex' : 'flex'}`}>
                  <div className="p-6 border-b border-slate-100 bg-white">
                    <h3 className="text-sm font-black uppercase tracking-widest text-slate-800 mb-4">Inbox</h3>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                      <input
                        type="text"
                        placeholder="Search colleagues..."
                        className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200/60 rounded-xl text-xs font-bold outline-none focus:ring-2 focus:ring-green-500 shadow-sm"
                        value={chatSearch}
                        onChange={e => setChatSearch(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar">
                    {loadingChat ? (
                      <div className="flex flex-col items-center justify-center py-20">
                        <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    ) : contacts.filter(c => c.fullName?.toLowerCase().includes(chatSearch.toLowerCase())).length === 0 ? (
                      <div className="text-center py-12 text-slate-400 text-xs font-bold uppercase tracking-wider">No contacts found</div>
                    ) : (
                      contacts.filter(c => c.fullName?.toLowerCase().includes(chatSearch.toLowerCase())).map(contact => {
                        const isSelected = selectedContact?._id === contact._id;
                        return (
                          <button
                            key={contact._id}
                            onClick={() => handleSelectContact(contact)}
                            className={`w-full flex items-center gap-4 p-4 rounded-2xl transition-all text-left ${isSelected ? 'bg-green-600 text-white shadow-lg shadow-green-500/20' : 'bg-white hover:bg-slate-100 border border-slate-100/50'}`}
                          >
                            <div className="relative flex-shrink-0">
                              <div className="w-12 h-12 rounded-full overflow-hidden border border-slate-200 bg-slate-100 flex items-center justify-center">
                                {contact.profileImage && (typeof contact.profileImage === 'string' || contact.profileImage.url) ? (
                                  <img src={publicAPI.getFullImageUrl(typeof contact.profileImage === 'string' ? contact.profileImage : contact.profileImage.url)} className="w-full h-full object-cover" alt="" />
                                ) : (
                                  <span className={`text-lg font-black uppercase ${isSelected ? 'text-green-200' : 'text-slate-400'}`}>
                                    {contact.fullName?.charAt(0)}
                                  </span>
                                )}
                              </div>
                              <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${contact.role === 'admin' ? 'bg-purple-500' : 'bg-green-500'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-baseline mb-1">
                                <h4 className={`text-xs font-black truncate uppercase tracking-tight ${isSelected ? 'text-white' : 'text-slate-800'}`}>{contact.fullName}</h4>
                                <span className={`text-[8px] font-bold ${isSelected ? 'text-green-200' : 'text-slate-400'}`}>
                                  {contact.lastMessage ? new Date(contact.lastMessage.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                </span>
                              </div>
                              <div className="flex justify-between items-center">
                                <p className={`text-[10px] truncate ${isSelected ? 'text-green-100' : 'text-slate-500'} max-w-[160px]`}>
                                  {contact.lastMessage ? contact.lastMessage.content : 'No messages yet'}
                                </p>
                                {contact.unreadCount > 0 && (
                                  <span className="bg-red-500 text-white text-[8px] font-black w-4.5 h-4.5 rounded-full flex items-center justify-center border border-white px-1 animate-pulse">
                                    {contact.unreadCount}
                                  </span>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Messages Chat Stream Column */}
                <div className={`flex-1 flex flex-col h-full bg-white ${!selectedContact ? 'hidden md:flex items-center justify-center' : 'flex'}`}>
                  {selectedContact ? (
                    <>
                      {/* Active Contact Header */}
                      <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/20">
                        <div className="flex items-center gap-4">
                          <button onClick={() => setSelectedContact(null)} className="md:hidden p-2 text-slate-500 hover:bg-slate-100 rounded-lg"><ArrowLeft className="w-5 h-5" /></button>
                          <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center">
                            {selectedContact.profileImage && (typeof selectedContact.profileImage === 'string' || selectedContact.profileImage.url) ? (
                              <img src={publicAPI.getFullImageUrl(typeof selectedContact.profileImage === 'string' ? selectedContact.profileImage : selectedContact.profileImage.url)} className="w-full h-full object-cover" alt="" />
                            ) : (
                              <span className="text-lg font-black text-slate-400 uppercase">
                                {selectedContact.fullName?.charAt(0)}
                              </span>
                            )}
                          </div>
                          <div>
                            <h4 className="text-xs font-black text-slate-800 uppercase tracking-tight">{selectedContact.fullName}</h4>
                            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{selectedContact.role?.replace('_', ' ')}</span>
                          </div>
                        </div>
                      </div>

                      {/* Message History bubble container */}
                      <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar bg-slate-50/20">
                        {loadingMessages ? (
                          <div className="flex items-center justify-center py-20">
                            <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        ) : messages.length === 0 ? (
                          <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                            <MessageSquare className="w-10 h-10 mb-2 opacity-30" />
                            <p className="text-[10px] font-black uppercase tracking-widest">Say hello to {selectedContact.fullName}!</p>
                          </div>
                        ) : (
                          messages.map((msg, i) => {
                            const isMe = msg.sender === admin?._id || msg.sender?._id === admin?._id || msg.sender === admin?.id;
                            return (
                              <div key={msg._id || i} className={`flex ${isMe ? 'justify-end' : 'justify-start'} animate-fade-in`}>
                                <div className={`max-w-[70%] p-4 rounded-2xl shadow-sm text-xs font-semibold leading-relaxed ${isMe ? 'bg-gradient-to-br from-green-600 to-green-800 text-white rounded-tr-none' : 'bg-slate-100 text-slate-800 rounded-tl-none border border-slate-200/50'}`}>
                                  {msg.attachment?.url && (
                                    msg.attachment.fileType === 'image' ? (
                                      <a href={publicAPI.getFullImageUrl(msg.attachment.url)} target="_blank" rel="noreferrer">
                                        <img src={publicAPI.getFullImageUrl(msg.attachment.url)} alt={msg.attachment.originalName} className="rounded-xl max-w-full max-h-48 object-cover mb-2 border border-white/20" />
                                      </a>
                                    ) : (
                                      <a href={publicAPI.getFullImageUrl(msg.attachment.url)} target="_blank" rel="noreferrer" className={`flex items-center gap-2 p-2.5 rounded-xl mb-2 ${isMe ? 'bg-white/15 hover:bg-white/25' : 'bg-white hover:bg-slate-50 border border-slate-200'} transition-colors`}>
                                        <FileText className="w-5 h-5 flex-shrink-0" />
                                        <span className="truncate text-[10px] font-bold">{msg.attachment.originalName || 'Download File'}</span>
                                        <Download className="w-4 h-4 flex-shrink-0 ml-auto" />
                                      </a>
                                    )
                                  )}
                                  {msg.content && <p>{msg.content}</p>}
                                  <div className="flex justify-end items-center gap-1 mt-1 text-[8px] opacity-70">
                                    <span>{new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                    {isMe && (
                                      <CheckCheck className={`w-3.5 h-3.5 ${msg.read ? 'text-green-200' : 'text-slate-300'}`} />
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                        <div ref={messagesEndRef} />
                      </div>

                      {/* File Preview Chip */}
                      {chatAttachment && (
                        <div className="px-4 pt-3 pb-0 bg-white border-t border-slate-100">
                          <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-3 py-2 text-xs font-bold text-green-700">
                            <Paperclip className="w-3.5 h-3.5" />
                            <span className="truncate flex-1">{chatAttachment.name}</span>
                            <button type="button" onClick={() => setChatAttachment(null)} className="p-0.5 hover:bg-green-100 rounded-full"><X className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>
                      )}
                      {/* Message Send Form Footer */}
                      <form onSubmit={handleSendMessage} className="p-4 border-t border-slate-100 flex gap-3 bg-white">
                        <input type="file" ref={chatFileInputRef} className="hidden" onChange={e => { if (e.target.files[0]) setChatAttachment(e.target.files[0]); e.target.value = ''; }} />
                        <button
                          type="button"
                          onClick={() => chatFileInputRef.current?.click()}
                          className="p-3.5 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-2xl transition-all flex items-center justify-center"
                          title="Attach a file"
                        >
                          <Paperclip className="w-5 h-5" />
                        </button>
                        <input
                          type="text"
                          placeholder="Type your message here..."
                          className="flex-1 px-4 py-3.5 bg-slate-50 border border-slate-200/60 rounded-2xl text-xs font-bold outline-none focus:ring-2 focus:ring-green-500 shadow-inner"
                          value={newMessage}
                          onChange={e => setNewMessage(e.target.value)}
                        />
                        <button
                          type="submit"
                          disabled={sendingMsg || (!newMessage.trim() && !chatAttachment)}
                          className="p-3.5 bg-green-600 text-white rounded-2xl shadow-lg shadow-green-500/20 hover:bg-green-700 transition-all flex items-center justify-center disabled:opacity-50"
                        >
                          <Send className="w-5 h-5" />
                        </button>
                      </form>
                    </>
                  ) : (
                    <div className="text-center p-8 space-y-4">
                      <div className="w-20 h-20 bg-green-50 text-green-600 rounded-[2rem] flex items-center justify-center mx-auto shadow-inner">
                        <MessageSquare className="w-10 h-10" />
                      </div>
                      <h4 className="text-xs font-black uppercase tracking-[0.2em] text-slate-800">Secure Direct Messaging</h4>
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest max-w-sm leading-relaxed">Select a teacher colleague or administrator profile from the list to start a confidential dialogue.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AdminPortal;